/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import { LoaderLayout } from "components/atoms/loader/LoaderLayout";
import { MEDIA_QUERY_FOR_PC } from "constants/mediaQueryForPc";
import { useGetPlaceList } from "hooks/event/useGetPlaceList";
import { useI18n } from "hooks/i18n/i18n";
import React, { useState } from "react";
import { BreadcrumbNavigation } from "ui/BreadcrumbNavigation";
import { changeStyleWithHosting } from "utility/hosting";

import { AccountLotteryItem } from "./AccountLotteryItem";
import { useGetLotteryTicketList } from "./useGetLotteryTicketList";

type HistoryFilter = "all" | "inProgress";

export const AccountApplicationHistory: React.FC = () => {
  const { t } = useI18n();
  const classes = styles();
  const [selectedFilter, setSelectedFilter] = useState<HistoryFilter>("all");

  // 通常の抽選申込履歴を取得する。
  const { lotteryTicketList, isLoading } = useGetLotteryTicketList();
  /**
   * @todo リセール申込履歴を取得する。
   * const { data, isLoading } = useGetResaleApplication();
   */

  // Place情報を取得する
  const placeIds = lotteryTicketList?.flatMap((ticket) =>
    ticket.tickets.map((t) => t.ticket.place)
  );
  const { data: places } = useGetPlaceList(placeIds);

  if (isLoading) {
    return <LoaderLayout />;
  }

  return (
    <div id="contents" css={classes.page}>
      <BreadcrumbNavigation
        breadcrumbs={[
          { label: t("common.routes.home"), link: "/" },
          { label: t("common.routes.account"), link: "/account" },
          { label: t("account.history.applicationHistory"), link: null },
        ]}
      />

      <main css={classes.main}>
        <h1 css={classes.heading}>{t("account.history.applicationHistory")}</h1>

        <div css={classes.filters}>
          <button
            type="button"
            aria-pressed={selectedFilter === "all"}
            css={classes.filterButton(selectedFilter === "all")}
            onClick={() => setSelectedFilter("all")}
          >
            {t("account.history.all")}
          </button>
          <button
            type="button"
            aria-pressed={selectedFilter === "inProgress"}
            css={classes.filterButton(selectedFilter === "inProgress")}
            onClick={() => setSelectedFilter("inProgress")}
          >
            {t("account.history.entryInProgress")}
          </button>
        </div>

        {lotteryTicketList?.length === 0 ? (
          <p css={classes.empty}>{t("account.history.empty")}</p>
        ) : (
          <ul css={classes.applicationList}>
            {lotteryTicketList.map((lotteryTicket) => (
              <AccountLotteryItem
                key={`lottery_${lotteryTicket.event._id ?? ""}`}
                event={lotteryTicket.event}
                tickets={lotteryTicket.tickets}
                places={places}
              />
            ))}
          </ul>
        )}
      </main>
    </div>
  );
};

const styles = () => {
  const { accentColor } = changeStyleWithHosting().baseStyles;

  return {
    page: css({
      minHeight: "calc(100vh - 436px)",
      backgroundColor: "#f4f4f4",
      [MEDIA_QUERY_FOR_PC]: {
        minHeight: "calc(100vh - 280px)",
      },
    }),
    main: css({
      width: "calc(100% - 32px)",
      maxWidth: "980px",
      padding: "32px 0 80px",
      margin: "0 auto",
      [MEDIA_QUERY_FOR_PC]: {
        paddingTop: "44px",
      },
    }),
    heading: css({
      margin: 0,
      fontSize: "24px",
      fontWeight: "bold",
      lineHeight: 1.5,
    }),
    filters: css({
      display: "flex",
      alignItems: "center",
      margin: "20px 0 16px",
    }),
    filterButton: (isSelected: boolean) =>
      css({
        minWidth: "80px",
        height: "40px",
        padding: "0 16px",
        color: isSelected ? "#fff" : "#111",
        fontSize: "14px",
        fontWeight: "bold",
        lineHeight: 1,
        backgroundColor: isSelected ? accentColor : "transparent",
        border: 0,
        borderRadius: "20px",
        cursor: "pointer",
      }),
    applicationList: css({
      display: "flex",
      flexDirection: "column",
      gap: "16px",
      padding: 0,
      margin: 0,
      listStyle: "none",
    }),
    empty: css({
      margin: 0,
      fontSize: "14px",
      fontWeight: "bold",
      lineHeight: 1.5,
    }),
  };
};
